import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Pencil, Eye, Heart, ExternalLink, Github, Youtube, Award, GitPullRequestArrow, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { DEPARTMENTS, CATEGORIES } from "./SubmissionModal";

const STATUS_STYLE = {
  pending: "border-amber-300 bg-amber-50 text-amber-600",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-600",
  rejected: "border-rose-300 bg-rose-50 text-rose-500",
};

const inputCls =
  "w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#2563EB] focus:bg-white";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400";

function Chip({ children }) {
  return (
    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{children}</span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="text-sm leading-relaxed text-slate-700">{children}</div>
    </div>
  );
}

export default function AdminProjectModal({ project, onClose, onSaved, endpoint, startEditing = false, isAdmin = true }) {
  const { t } = useLang();
  const [editing, setEditing] = useState(startEditing);
  const [busy, setBusy] = useState(false);
  const join = (arr) => (arr || []).join(", ");
  const [form, setForm] = useState({
    title: project.title || "", tagline: project.tagline || "", description: project.description || "",
    department: project.department || "", category: project.category || "",
    team_members: join(project.team_members), team_emails: join(project.team_emails), tech_stack: join(project.tech_stack),
    demo_url: project.demo_url || "", github_url: project.github_url || "", video_url: project.video_url || "",
    award: project.award || "", image_url: project.image_url || "",
  });
  const [file, setFile] = useState(null);

  const saveUrl = endpoint || `/admin/projects/${project.id}`;
  const deptOptions = DEPARTMENTS.includes(form.department) || !form.department ? DEPARTMENTS : [...DEPARTMENTS, form.department];
  const catOptions = CATEGORIES.includes(form.category) || !form.category ? CATEGORIES : [...CATEGORIES, form.category];

  const FIELD_LABELS = {
    title: t.sub_f_title, tagline: t.sub_f_tagline, description: t.sub_f_desc,
    department: t.sub_f_dept, category: t.sub_f_cat, team_members: t.sub_f_team,
    team_emails: t.sub_f_emails, tech_stack: t.sub_f_tech, demo_url: t.sub_f_demo,
    github_url: t.sub_f_github, video_url: t.sub_f_video, award: t.sub_f_award, image_url: t.sub_f_image,
  };

  const pendingChanges = project.pending_edit
    ? Object.keys(FIELD_LABELS).filter(
        (k) => JSON.stringify(project.pending_edit[k] ?? "") !== JSON.stringify(project[k] ?? "")
      )
    : [];

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.department || !form.category) {
      toast.error("Title, description, department and category are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === "image_url" && file ? "" : v));
      if (file) fd.append("image", file);
      const r = await api.put(saveUrl, fd);
      toast.success(isAdmin ? "Project updated" : project.status === "approved" ? "Edits sent for faculty review" : "Project updated");
      setEditing(false);
      setFile(null);
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const reviewEdit = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/projects/${project.id}/${action}`);
      toast.success(action === "approve-edit" ? "Changes approved — now live" : "Proposed changes discarded");
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fmtVal = (v) => (Array.isArray(v) ? v.join(", ") : String(v ?? ""));

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="admin-project-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        data-lenis-prevent
        className="max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img src={file ? URL.createObjectURL(file) : resolveImage(project.image_url)} alt={project.title} className="h-52 w-full rounded-t-xl object-cover sm:h-64" data-testid="modal-project-image" />
          <button onClick={onClose} data-testid="modal-close-button" className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-600 shadow transition-colors hover:text-slate-900">
            <X size={16} />
          </button>
          <span className={`absolute left-4 top-4 rounded-full border px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] backdrop-blur ${STATUS_STYLE[project.status]}`} data-testid="modal-status-badge">
            {project.status}
          </span>
        </div>

        <div className="p-6 sm:p-8">
          {!editing ? (
            <div data-testid="modal-view-mode">
              {isAdmin && project.pending_edit && (
                <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="pending-edit-panel">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700">
                    <GitPullRequestArrow size={13} /> {t.adm_pending_edit_title}
                  </p>
                  <div className="mt-3 space-y-2">
                    {pendingChanges.map((k) => (
                      <div key={k} className="text-xs" data-testid={`pending-change-${k}`}>
                        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber-600">{FIELD_LABELS[k]}</span>
                        <p className="mt-0.5 text-slate-500 line-through decoration-rose-400/60">{fmtVal(project[k]) || "—"}</p>
                        <p className="whitespace-pre-line font-medium text-slate-800">{fmtVal(project.pending_edit[k]) || "—"}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex gap-2.5">
                    <button onClick={() => reviewEdit("approve-edit")} disabled={busy} data-testid="approve-edit-button" className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-emerald-500 disabled:opacity-50">
                      <Check size={12} /> {t.adm_approve_edit}
                    </button>
                    <button onClick={() => reviewEdit("discard-edit")} disabled={busy} data-testid="discard-edit-button" className="flex items-center gap-1.5 rounded-md border border-rose-300 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-rose-500 transition-colors hover:bg-rose-500 hover:text-white disabled:opacity-50">
                      <Trash2 size={12} /> {t.adm_discard_edit}
                    </button>
                  </div>
                </div>
              )}

              <h2 className="font-display text-2xl font-bold text-slate-900" data-testid="modal-title">{project.title}</h2>
              {project.tagline && <p className="mt-1 text-sm text-slate-500">{project.tagline}</p>}

              <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                <Field label={t.auth_dept}>{project.department}</Field>
                <Field label={t.sub_f_cat}>{project.category}</Field>
                <Field label={t.adm_submitted_by}>
                  {project.submitter_name || "—"}
                  {project.submitter_student_id && <span className="ml-1 font-mono text-xs text-slate-400">({project.submitter_student_id})</span>}
                </Field>
                <Field label={t.news_f_date}>{(project.created_at || "").slice(0, 10)}</Field>
                <Field label={t.stat_views}><span className="inline-flex items-center gap-1"><Eye size={13} className="text-slate-400" /> {project.views}</span></Field>
                <Field label={t.stat_likes}><span className="inline-flex items-center gap-1"><Heart size={13} className="text-slate-400" /> {project.likes}</span></Field>
              </div>

              <div className="mt-5">
                <p className={labelCls}>{t.sub_f_desc}</p>
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid="modal-description">{project.description}</p>
              </div>

              {(project.team_members || []).length > 0 && (
                <div className="mt-5">
                  <p className={labelCls}>{t.team_label}</p>
                  <div className="flex flex-wrap gap-2">{project.team_members.map((m) => <Chip key={m}>{m}</Chip>)}</div>
                </div>
              )}
              {(project.team_emails || []).length > 0 && (
                <div className="mt-5">
                  <p className={labelCls}>{t.sub_f_emails}</p>
                  <div className="flex flex-wrap gap-2">{project.team_emails.map((m) => <Chip key={m}>{m}</Chip>)}</div>
                </div>
              )}
              {(project.tech_stack || []).length > 0 && (
                <div className="mt-5">
                  <p className={labelCls}>{t.tech_label}</p>
                  <div className="flex flex-wrap gap-2">{project.tech_stack.map((s) => <Chip key={s}>{s}</Chip>)}</div>
                </div>
              )}

              {(project.demo_url || project.github_url || project.video_url) && (
                <div className="mt-5">
                  <p className={labelCls}>{t.adm_links}</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="modal-links">
                    {project.demo_url && (
                      <a href={project.demo_url} target="_blank" rel="noreferrer" data-testid="modal-demo-link" className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs text-[#1E3A8A] transition-colors hover:border-[#2563EB]/50">
                        <ExternalLink size={12} /> {t.demo_link}
                      </a>
                    )}
                    {project.github_url && (
                      <a href={project.github_url} target="_blank" rel="noreferrer" data-testid="modal-github-link" className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs text-[#1E3A8A] transition-colors hover:border-[#2563EB]/50">
                        <Github size={12} /> GitHub
                      </a>
                    )}
                    {project.video_url && (
                      <a href={project.video_url} target="_blank" rel="noreferrer" data-testid="modal-video-link" className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs text-[#1E3A8A] transition-colors hover:border-[#2563EB]/50">
                        <Youtube size={12} /> {t.video_label}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {project.award && (
                <div className="mt-5">
                  <p className={labelCls}>{t.sub_f_award}</p>
                  <p className="flex items-center gap-1.5 text-sm text-amber-600"><Award size={14} /> {project.award}</p>
                </div>
              )}
              {project.status === "rejected" && project.feedback && (
                <p className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">Feedback: {project.feedback}</p>
              )}

              <button onClick={() => setEditing(true)} data-testid="modal-edit-button" className="mt-7 flex items-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB]">
                <Pencil size={12} /> {t.news_edit_btn}
              </button>
            </div>
          ) : (
            <div data-testid="modal-edit-mode">
              <h2 className="font-display text-xl font-bold text-slate-900">{t.adm_edit_project}</h2>
              {!isAdmin && project.status === "approved" && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-700" data-testid="edit-staged-note">
                  {t.my_edit_staged_note}
                </p>
              )}
              {!isAdmin && project.status === "rejected" && (
                <p className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-[#1E3A8A]" data-testid="edit-resubmit-note">
                  {t.my_edit_resubmit_note}
                </p>
              )}
              <div className="mt-5 space-y-4">
                <div>
                  <label className={labelCls}>{t.sub_f_title} *</label>
                  <input className={inputCls} value={form.title} onChange={set("title")} data-testid="edit-title-input" />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_tagline}</label>
                  <input className={inputCls} value={form.tagline} onChange={set("tagline")} data-testid="edit-tagline-input" />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_desc} *</label>
                  <textarea className={inputCls} rows={5} value={form.description} onChange={set("description")} data-testid="edit-description-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t.sub_f_dept} *</label>
                    <select className={inputCls} value={form.department} onChange={set("department")} data-testid="edit-department-select">
                      <option value="">{t.sub_f_dept_ph}</option>
                      {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_cat} *</label>
                    <select className={inputCls} value={form.category} onChange={set("category")} data-testid="edit-category-select">
                      <option value="">{t.sub_f_cat_ph}</option>
                      {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_team}</label>
                  <input className={inputCls} value={form.team_members} onChange={set("team_members")} placeholder={t.sub_f_team_ph} data-testid="edit-team-input" />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_emails}</label>
                  <input className={inputCls} value={form.team_emails} onChange={set("team_emails")} placeholder={t.sub_f_emails_ph} data-testid="edit-emails-input" />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_tech}</label>
                  <input className={inputCls} value={form.tech_stack} onChange={set("tech_stack")} placeholder={t.sub_f_tech_ph} data-testid="edit-tech-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className={labelCls}>{t.sub_f_demo}</label>
                    <input className={inputCls} value={form.demo_url} onChange={set("demo_url")} data-testid="edit-demo-input" />
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_github}</label>
                    <input className={inputCls} value={form.github_url} onChange={set("github_url")} data-testid="edit-github-input" />
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_video}</label>
                    <input className={inputCls} value={form.video_url} onChange={set("video_url")} data-testid="edit-video-input" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_award}</label>
                  <input className={inputCls} value={form.award} onChange={set("award")} placeholder={t.sub_f_award_ph} data-testid="edit-award-input" />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_image}</label>
                  <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="edit-image-file-input" className="w-full text-sm text-slate-500 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-4 file:py-2.5 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.15em] file:text-slate-600 hover:file:bg-slate-200" />
                  {!file && (
                    <input className={`${inputCls} mt-3`} value={form.image_url} onChange={set("image_url")} placeholder={t.sub_f_imgurl_ph} data-testid="edit-image-url-input" />
                  )}
                </div>
                <div className="flex gap-2.5 pt-2">
                  <button onClick={save} disabled={busy} data-testid="modal-save-button" className="rounded-full bg-emerald-600 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-emerald-500 disabled:opacity-50">
                    {busy ? t.creds_saving : t.news_update}
                  </button>
                  <button onClick={() => { setEditing(false); setFile(null); if (startEditing) onClose(); }} data-testid="modal-cancel-button" className="rounded-full border border-slate-200 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 transition-colors hover:text-slate-800">
                    {t.news_cancel}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
